import { PaymentType } from '@/features/graphql/generated/graphql'
import {
    ActualAmountFieldName,
    FormKeys,
    paymentTypeFieldNames,
} from '@/features/shiftCloseouts/form/constants'
import { schema } from '@/features/shiftCloseouts/form/schema'
import { FormInput, FormOutput, OnFormSubmit } from '@/features/shiftCloseouts/form/types'
import { getDefaultValues } from '@/features/shiftCloseouts/form/utils/getDefaultValues'
import { ShiftCloseoutPaymentMethod } from '@/features/shiftCloseouts/types'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    UseFormReturn,
    useController,
    useFormContext,
    useForm as useReactHookForm,
    useWatch,
} from 'react-hook-form'

export const useForm = (
    paymentMethods: ShiftCloseoutPaymentMethod[]
): UseFormReturn<FormInput, never, FormOutput> =>
    useReactHookForm<FormInput, never, FormOutput>({
        defaultValues: getDefaultValues(paymentMethods),
        mode: 'onChange',
        resolver: zodResolver<FormInput, never, FormOutput>(schema),
    })

// React Hook Form cannot resolve a nested field value through Zod's enum-keyed record.
interface ActualAmountControllerValues {
    actualAmounts: Record<`${PaymentType}`, string>
}

export const useActualAmountField = (
    paymentType: PaymentType
): {
    invalid: boolean
    onBlur: () => void
    onChange: (value: string) => void
    value: string
} => {
    const {
        field: { onBlur, onChange, value },
        fieldState: { invalid, isTouched },
    } = useController<ActualAmountControllerValues, ActualAmountFieldName>({
        name: paymentTypeFieldNames[paymentType],
    })

    return {
        invalid: isTouched && invalid,
        onBlur,
        onChange,
        value,
    }
}

export const useActualAmounts = (): FormInput['actualAmounts'] =>
    useWatch<FormInput, 'actualAmounts'>({ name: FormKeys.ActualAmounts })

export const useFormSubmit = (
    onFormSubmit: OnFormSubmit
): [{ isSubmitting: boolean; isValid: boolean }, () => void] => {
    const {
        formState: { isSubmitting, isValid },
        handleSubmit,
    } = useFormContext<FormInput, never, FormOutput>()

    return [{ isSubmitting, isValid }, handleSubmit(onFormSubmit)]
}
